import React, { useContext, useMemo, useState, useEffect, memo } from 'react';
import { ThemeContext } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { icons } from '../../../../assets';
import { GetColor, getStatus } from '../../../../utils';
import {
  formatPowerForBadge,
  formatFlowForBadge,
  getMarkerFillColor,
} from './marker-helpers';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  AlertTriangleIcon,
  getSeverityKey,
  formatTimeAgo,
  formatNumber,
} from './alarm-helpers';
import { Sparkline } from '../../../../components';
import * as s from './styled-pracas-mapa';

// Icones Lucide-style inline (X/Twitter aesthetic — line, monocromatico,
// stroke=currentColor pra herdar a cor do texto). Tamanho fixo 14x14
// pra usar no MetricRow ao lado do label. Ver CLAUDE.md secao 'Ícones'.
const ZapIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const DropletIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z" />
  </svg>
);

const ICON_COMPONENT = { zap: ZapIcon, droplet: DropletIcon };

// "ultima leitura ha X" a partir do lastSeen (ISO UTC). Degrada o "sem dados"
// binario: mostra se o gap e recente (link piscou) ou apagao longo. Null
// quando nao ha lastSeen.
const formatLastSeen = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const diff = Date.now() - t;
  if (diff < 60000) return 'agora';
  const min = Math.floor(diff / 60000);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return `há ${d}d`;
};

// Heuristica simples pra detectar status "sem dados" pelo label (tolerante
// a acento/variacao) — pra so anexar o "ultima leitura ha X" quando agrega.
const isSemDados = (label) =>
  (label || '').toLowerCase().includes('sem');

// Painel lateral de detalhe do parque selecionado. Substitui o AlertOverlay
// no canto top-right do mapa quando o operador clica num marker — em vez
// de abrir um InfoWindow flutuante (que cobre o mapa), o conteudo aparece
// num painel fixo, mesma posicao da lista de alertas.
//
// Beneficios:
//   - Nao polui o mapa (importante pra modo TV/fullscreen)
//   - Posicao consistente (operador nao precisa procurar onde apareceu)
//   - Mais espaco que o InfoWindow apertado — comporta sections de
//     informacao previa (tempo real, breakdown de equipamentos)
//
// Estrutura visual (top -> bottom):
//   ┌────────────────────────┐
//   │ ‹ EM ALERTA AGORA (N)  │  back-button
//   ├────────────────────────┤
//   │   ● STATUS             │  status + titulo + cliente + chips
//   │   Parque Nome          │
//   │   📍 Cliente            │
//   │  UCHA  N EQUIPAMENTOS  │
//   │ ────────────────────── │  separador
//   │   TEMPO REAL           │  metrics da subscription onUpdateIOT
//   │   ⚡ Potencia    X MW  │
//   │   💧 Vazao    Y l/s    │
//   │   EQUIPAMENTOS         │  breakdown por status
//   │   ● Rodando        N   │
//   │   ● Atencao        N   │
//   │   ● Alarme         N   │
//   │                        │  (espaco flexivel)
//   ├────────────────────────┤
//   │ [   Ver detalhes  ›  ] │  sempre no fundo do painel
//   └────────────────────────┘
//
// Props:
//   marker: o marker selecionado (com .position, .name, etc)
//   onClose: callback pra fechar o painel (deselecionar)
//   alertCount: numero de alertas ativos. Se > 0, o back-button leva
//     "de volta pra Em alerta agora (N)"; se 0, e so um botao "Fechar".
//   filterStatus: array de filtros (passado via dispatch ao clicar ver detalhes)
const MarkerDetailPanel = ({
  marker,
  onClose,
  alertCount,
  filterStatus,
  history: realtimeHistory = [],
  $cco = false,
  // Fundo transparente pro liquid glass do dock (telao) aparecer.
  glass = false,
}) => {
  const { colors } = useContext(ThemeContext);
  const dispatch = useDispatch();
  const history = useHistory();

  // Snapshot realtime do parque selecionado (alimentado pela subscription
  // onUpdateIOT via saga primeiro-nivel). Forma:
  //   { onTimeUcha, vazaoUcha, potenciaEba, vazaoEba }
  const parqueId = marker?.position?.id;
  const realtime = useSelector(
    (state) =>
      (parqueId && state.primeiroNivel.parqueRealtime?.[parqueId]) || null,
  );

  // IMPORTANT: hooks DEVEM rodar incondicionalmente, antes de qualquer
  // early return. React valida a ordem por chamada — early return mais
  // hook depois quebra a regra. Por isso position e os useMemo ficam
  // aqui em cima, com guarda interna (marker?.position) em cada um.
  const position = marker?.position;

  // Series do sparkline por field (chave no parqueRealtime). Extrai
  // do realtimeHistory (array de snapshots { onTimeUcha, ..., ts }).
  // Devolve mapa { fieldName: [valores] } pra cada metrica renderizar
  // sua propria mini sparkline ao lado.
  const sparklineSeriesByField = useMemo(() => {
    const series = {
      onTimeUcha: [],
      vazaoEba: [],
      vazaoUcha: [],
      potenciaEba: [],
    };
    realtimeHistory.forEach((point) => {
      Object.keys(series).forEach((field) => {
        let v = Number(point[field]);
        // onTimeUcha (geracao UCHA) vem NEGATIVO do backend; usamos o
        // modulo pra sparkline bater com o valor exibido (Math.abs acima).
        if (field === 'onTimeUcha') v = Math.abs(v);
        if (Number.isFinite(v)) series[field].push(v);
      });
    });
    return series;
  }, [realtimeHistory]);

  // Selo de frescor: timestamp mais recente do historico realtime. Tick de
  // 5s reavalia a idade mesmo SEM novos dados — e o que faz o selo virar
  // "stale/vermelho" quando a comunicacao da maquina para (a subscription
  // nao dispara re-render quando NAO chega nada).
  const [freshTick, setFreshTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setFreshTick((t) => t + 1), 5000);
    return () => clearInterval(id);
  }, []);
  const realtimeFreshness = useMemo(() => {
    const last = realtimeHistory.reduce(
      (mx, p) => (Number(p.ts) > mx ? Number(p.ts) : mx),
      0,
    );
    if (!last) return null;
    const ageSec = Math.max(0, Math.floor((Date.now() - last) / 1000));
    // 90s sem atualizacao = stale (mesmo limiar do ConnectionStatus global).
    return { ageSec, stale: ageSec > 90 };
    // freshTick entra na dep so pra forcar recalculo periodico.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realtimeHistory, freshTick]);

  // Metricas tempo real (de parqueRealtime). Cada metrica e renderizada
  // so se tiver valor != 0 (zerados sao indistinguiveis de "sem dado"
  // e poluem visualmente). Auto-escala kW->MW e l/s->m³/s via os
  // helpers compartilhados com o badge do marker. icon e um nome
  // ('zap'|'droplet') que mapeia pra componente SVG inline acima.
  // field: chave do parqueRealtime usada pra extrair a serie da history.
  const realtimeMetrics = useMemo(() => {
    if (!realtime) return [];
    const out = [];
    const power = Number(realtime.onTimeUcha);
    if (Number.isFinite(power) && power !== 0) {
      out.push({
        key: 'power-ucha',
        icon: 'zap',
        // UCHA = geracao. Math.abs porque o backend manda a potencia
        // gerada com sinal NEGATIVO (convencao do grid); exibimos o
        // modulo positivo, igual ao balao do mapa e ao card do dashboard
        // (senao aparecia "-257 kW" pro operador).
        label: 'Geração',
        value: formatPowerForBadge(Math.abs(power)),
        field: 'onTimeUcha',
      });
    }
    const flowEba = Number(realtime.vazaoEba);
    if (Number.isFinite(flowEba) && flowEba !== 0) {
      out.push({
        key: 'flow-eba',
        icon: 'droplet',
        label: 'Vazão',
        value: formatFlowForBadge(flowEba),
        field: 'vazaoEba',
      });
    }
    const flowUcha = Number(realtime.vazaoUcha);
    if (Number.isFinite(flowUcha) && flowUcha !== 0) {
      out.push({
        key: 'flow-ucha',
        icon: 'droplet',
        label: 'Vazão turb.',
        value: formatFlowForBadge(flowUcha),
        field: 'vazaoUcha',
      });
    }
    const powerEba = Number(realtime.potenciaEba);
    if (Number.isFinite(powerEba) && powerEba !== 0) {
      out.push({
        key: 'power-eba',
        icon: 'zap',
        label: 'Consumo',
        value: formatPowerForBadge(powerEba),
        field: 'potenciaEba',
      });
    }
    return out;
  }, [realtime]);

  // Lista individual das MAQUINAS (things) do parque — uma linha por
  // maquina, achatando appliance.thing[] de todas as appliances. Cada
  // thing carrega seu proprio status (StatusType { name, color }) que o
  // backend ja inclui no park/list (join appliance.thing.status), entao
  // o status e individual de verdade — nao o agregado da appliance.
  const machineList = useMemo(() => {
    const appliances = position?.appliance || [];
    if (!appliances.length) return [];
    const rows = [];
    appliances.forEach((appliance) => {
      const things = appliance.thing || [];
      things.forEach((th, idx) => {
        const st = th.status || null;
        rows.push({
          id: th.id != null ? th.id : `${appliance.id}-${idx}`,
          name: th.thing || th.name || 'Máquina',
          // StatusType.name e o rotulo legivel ('Rodando', 'Alarme/Erro',
          // 'Desligada'...) e color o hex canonico do status (fonte de
          // verdade no banco). Fallback 'sem dados' quando a thing nao
          // tem statustype associado.
          statusLabel: (st && st.name) || 'Sem dados',
          color: (st && st.color) || getMarkerFillColor('sem dados'),
          // lastSeen (ISO UTC) = instante da ultima leitura do device. Usado
          // pra "ultima leitura ha X" — degrada o "sem dados" binario, deixando
          // claro se e gap recente (link piscou) ou apagao longo.
          lastSeen: th.lastSeen || null,
        });
      });
    });
    return rows;
  }, [position]);

  // Early return: ate aqui ja rodamos todos os hooks. A partir daqui
  // codigo nao-hook (constantes derivadas, JSX). Seguro retornar.
  if (!marker || !position) return null;

  const statusLabel = getStatus(position.status);
  const errorCount = position.status && position.status.E;

  // Tipo de equipamento "destaque" (mesmo da InfoWindow original) — pega
  // o appliance cujo status casa com o status agregado do parque, ou
  // 'Equipamento' como fallback.
  const getFeaturedApplianceType = () => {
    if (!position.appliance) return 'Equipamento';
    const featured = position.appliance.find(
      (appliance) => getStatus(appliance.status) === getStatus(position.status),
    );
    return (featured && featured.type && featured.type.type) || 'Equipamento';
  };
  const applianceType = getFeaturedApplianceType();

  const typeDotColor =
    applianceType === 'ucha'
      ? colors.orange
      : applianceType === 'bomba'
      ? colors.blue
      : colors.label;

  const handleSeeDetails = () => {
    // Dispatch sempre — alimenta o parqueSelecionado em Redux pra
    // breadcrumb conseguir voltar 'Parque' independente do destino.
    dispatch({
      type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
      filterStatus,
      parqueSelecionado: position,
      clienteSelecionado: position.customer,
    });
    const appliances = (position && position.appliance) || [];
    if (appliances.length === 1) {
      // Atalho UX: parque com 1 unica aplicacao → pula a tela
      // intermediaria de lista e vai direto pro detalhe. Mesma shape
      // do state que aplicacoes-cliente.js empurra ({ ...aplicacao,
      // ...parqueSelecionado }) — aplicacao.js le
      // history.location.state como produto.
      history.push('/dashboard/customer/plant', {
        ...appliances[0],
        ...position,
      });
    } else {
      // Multiplas aplicacoes — operador escolhe na lista da tela
      // intermediaria (/dashboard/customer).
      history.push('/dashboard/customer', { ...position.customer });
    }
  };

  const backLabel =
    alertCount > 0 ? `Em alerta agora (${alertCount})` : 'Fechar';

  // Causa principal do alerta — vem do backend (topAlarm) quando o park
  // tem alguma variavel disparando. Renderiza um card destacado antes
  // das sections normais (Tempo real, Equipamentos) pra responder a
  // pergunta "por que esta em alerta?" sem o operador precisar abrir
  // o detalhe da aplicacao.
  const topAlarm = position.topAlarm || marker.topAlarm;
  const topAlarmSeverity = topAlarm ? getSeverityKey(topAlarm.level) : null;

  const hasExtraSections =
    !!topAlarm || realtimeMetrics.length > 0 || machineList.length > 0;

  return (
    <s.MarkerDetailPanel role="dialog" aria-label="Detalhes do parque" $cco={$cco} $glass={glass}>
      <s.DetailBackButton type="button" onClick={onClose}>
        {backLabel}
      </s.DetailBackButton>
      <s.DetailContent>
        {/* Header: pill de status + badge de erros */}
        <s.MarkerHeader>
          <s.StatusBadge background={GetColor(statusLabel)}>
            {statusLabel}
          </s.StatusBadge>
          {!!errorCount && (
            <s.ErrorBadge title={`${errorCount} erro(s)`}>
              {errorCount}
            </s.ErrorBadge>
          )}
        </s.MarkerHeader>

        {/* Titulo do parque + cliente. Usamos position.name (= marker
            original do map iteration). NAO usar marker.name aqui porque
            google-maps-react tem defaultProps { name: 'Marker' }, entao
            selectedMarker.props.name retorna a string literal "Marker"
            quando nao e explicitamente passado. */}
        <s.ParkTitle title={position.name}>{position.name}</s.ParkTitle>
        <s.CustomerSubtitle>
          <img src={icons.ClientPin} alt="" />
          {position.customer?.name}
        </s.CustomerSubtitle>

        {/* Chips: tipo de equipamento + qtd */}
        <s.ChipRow>
          <s.Chip dotColor={typeDotColor}>
            <span className="dot" />
            {applianceType}
          </s.Chip>
          <s.Chip>
            <strong>{position.applianceAmount}</strong>
            equipamentos
          </s.Chip>
        </s.ChipRow>

        {hasExtraSections && <s.DetailSeparator />}

        {/* Section: causa do alerta — card destacado quando o backend
            envia topAlarm. Aparece primeiro pra triagem rapida do
            operador (a pergunta-chave "por que esta em alerta?"). */}
        {topAlarm && (
          <>
            <s.DetailSectionLabel>Causa do alerta</s.DetailSectionLabel>
            <s.CauseCard $severity={topAlarmSeverity}>
              <s.CauseHeader $severity={topAlarmSeverity}>
                {/* Direcao high/low: range e per-variable firmware tem;
                    word bit-packed (camada 3) cai no triangulo generico. */}
                {topAlarm.direction === 'low' ? (
                  <ArrowDownIcon size={14} />
                ) : topAlarm.direction === 'high' ? (
                  <ArrowUpIcon size={14} />
                ) : (
                  <AlertTriangleIcon size={14} />
                )}
                <span className="variable" title={topAlarm.variable}>
                  {topAlarm.variable}
                </span>
                {/* Valor numerico so faz sentido quando vem do range; o
                    firmware reporta um bit ativo sem leitura associada. */}
                {topAlarm.source !== 'firmware' && topAlarm.value != null && (
                  <span className="value">
                    {formatNumber(topAlarm.value)}
                    {topAlarm.unit ? ` ${topAlarm.unit}` : ''}
                  </span>
                )}
              </s.CauseHeader>
              <s.CauseMeta>
                {topAlarm.applianceName && (
                  <span title={topAlarm.applianceName}>
                    {topAlarm.applianceName}
                  </span>
                )}
                {topAlarm.source !== 'firmware' &&
                  topAlarm.threshold != null && (
                    <span className="threshold">
                      limite {topAlarm.direction === 'low' ? '<' : '>'}{' '}
                      {formatNumber(topAlarm.threshold)}
                      {topAlarm.unit ? ` ${topAlarm.unit}` : ''}
                    </span>
                  )}
                {topAlarm.source === 'firmware' && (
                  <span className="threshold" title="Alarme reportado pelo firmware do dispositivo">
                    Alarme do dispositivo
                  </span>
                )}
                {formatTimeAgo(topAlarm.since) && (
                  <span className="ago">{formatTimeAgo(topAlarm.since)}</span>
                )}
              </s.CauseMeta>
            </s.CauseCard>
          </>
        )}

        {/* Section: metricas tempo real (somente se ha valor) */}
        {realtimeMetrics.length > 0 && (
          <>
            <s.DetailSectionHead>
              <s.DetailSectionLabel>Tempo real</s.DetailSectionLabel>
              {realtimeFreshness && (
                <s.RealtimeFreshness
                  $stale={realtimeFreshness.stale}
                  title={
                    realtimeFreshness.stale
                      ? 'Sem atualização recente — comunicação pode ter caído'
                      : 'Dado ao vivo'
                  }
                >
                  <span className="dot" />
                  {realtimeFreshness.ageSec < 10
                    ? 'agora'
                    : realtimeFreshness.ageSec < 60
                    ? `há ${realtimeFreshness.ageSec}s`
                    : `há ${Math.floor(realtimeFreshness.ageSec / 60)}min`}
                </s.RealtimeFreshness>
              )}
            </s.DetailSectionHead>
            {/* Metricas em CARDS (valor em destaque) — antes era linha com o
                valor distante do label; o card agrupa label+valor+tendencia. */}
            <s.MetricCards>
              {realtimeMetrics.map((m) => {
                const Icon = ICON_COMPONENT[m.icon];
                const series = sparklineSeriesByField[m.field] || [];
                return (
                  <s.MetricCard key={m.key}>
                    <span className="top">
                      {Icon && (
                        <span className="ic">
                          <Icon />
                        </span>
                      )}
                      {m.label}
                    </span>
                    <span className="val">{m.value}</span>
                    {series.length >= 2 && (
                      <s.MetricCardSpark>
                        <Sparkline
                          data={series}
                          width={120}
                          height={20}
                          color={m.icon === 'zap' ? colors.orange : colors.blue}
                          fill
                        />
                      </s.MetricCardSpark>
                    )}
                  </s.MetricCard>
                );
              })}
            </s.MetricCards>
          </>
        )}

        {/* Section: maquinas individuais — nome + status proprio */}
        {machineList.length > 0 && (
          <>
            <s.DetailSectionLabel>
              Equipamentos ({machineList.length})
            </s.DetailSectionLabel>
            <s.MetricsList>
              {machineList.map((row) => {
                // "ultima leitura ha X": so anexamos em SEM DADOS (onde agrega
                // contexto — gap recente vs apagao). Nos demais o dado e fresco
                // por definicao (status ao vivo), entao nao poluimos.
                const ago = isSemDados(row.statusLabel)
                  ? formatLastSeen(row.lastSeen)
                  : null;
                return (
                  <s.ApplianceRow key={row.id} color={row.color}>
                    <span className="dot" />
                    <span className="name" title={row.name}>
                      {row.name}
                    </span>
                    <span className="status">
                      {row.statusLabel}
                      {ago && (
                        <span className="ago"> · última leitura {ago}</span>
                      )}
                    </span>
                  </s.ApplianceRow>
                );
              })}
            </s.MetricsList>
          </>
        )}
      </s.DetailContent>

      {/* CTA principal — sempre no fundo do painel, fora do DetailContent
          (que pode scrollar). Garante que o botao "Ver detalhes" e o
          ultimo elemento visivel sem depender de altura do conteudo. */}
      <s.SeeMore type="button" onClick={handleSeeDetails}>
        Ver detalhes
      </s.SeeMore>
    </s.MarkerDetailPanel>
  );
};

// memo: mesma razao do AlertOverlay — evita re-render a cada tick de
// polling do PracasMapa quando o marker selecionado e seu history nao
// mudaram. O mini-grafico/sparklines sao caros de recomputar.
export default memo(MarkerDetailPanel);
